import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import Spinner from './Spinner';
import OrganizationCard from './OrganizationCard';
import { useNavigate } from 'react-router-dom';

/**
 * Reusable FindOrganizationsTab component
 * Props:
 *   - nameFilter (string): filter by organization name (optional)
 *   - locationFilter (string): filter by location (optional)
 *   - limitResults (number): max number of organizations to show (optional, default 6)
 *   - showHeader (boolean): show section header (default true)
 *   - headerText (string): custom header text (optional)
 */
const FindOrganizationsTab = ({
  nameFilter = '',
  locationFilter = '',
  limitResults = 6, // Default to showing 6 profiles initially
  showHeader = true,
  headerText = 'Find Organizations',
  subheaderText = 'Browse and connect with organizations',
  showSearchControls = true,
  onLoadMoreClick, // Optional callback for when Load More is clicked
}) => {
  const navigate = useNavigate();
  const [allOrganizations, setAllOrganizations] = useState([]);
  const [filteredOrganizations, setFilteredOrganizations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState({ name: '', city: '' });
  
  // Constants for pagination
  const INITIAL_PAGE_SIZE = 6; // Always show 6 profiles initially
  const PAGE_SIZE = 6; // Load 6 more profiles each time

  const fetchOrganizations = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Check if Firebase is properly initialized
      if (!db) {
        throw new Error('Firebase Firestore is not properly initialized');
      }
      
      const organizationsRef = collection(db, 'organizations');
      
      // Fetch all organizations and filter on client side for simplicity
      const q = query(organizationsRef);
      
      const querySnapshot = await getDocs(q);
      
      const defaultDate = new Date('2025-08-01T00:00:00'); // Default date for missing updatedAt
      
      let results = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Filter out inactive organizations on client side
        if (data && data.orgName && doc.id && 
            data.status && ['active', 'Active'].includes(data.status)) {
          // Handle different types of updatedAt values
          let updatedAt = defaultDate;
          const updatedAtValue = data.updatedAt;
          
          if (updatedAtValue) {
            if (typeof updatedAtValue.toDate === 'function') {
              updatedAt = updatedAtValue.toDate();
            } else if (updatedAtValue instanceof Date) {
              updatedAt = updatedAtValue;
            } else if (typeof updatedAtValue === 'string') {
              updatedAt = new Date(updatedAtValue) || defaultDate;
            } else if (typeof updatedAtValue === 'number') {
              updatedAt = new Date(updatedAtValue);
            }
          }
          
          results.push({ 
            id: doc.id, 
            ...data, 
            updatedAt // Ensure updatedAt is always a valid Date object
          });
        }
      });
      
      // Sort by updatedAt in descending order (newest first)
      results.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
      
      setAllOrganizations(results);
      
    } catch (err) {
      setError(`Failed to load organizations. ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Filtering and pagination
  useEffect(() => {
    let filtered = [...allOrganizations];
    
    if (search.name) {
      filtered = filtered.filter(
        org => org.orgName && org.orgName.toLowerCase().includes(search.name.toLowerCase())
      );
    }
    
    if (search.city) {
      filtered = filtered.filter(
        org => org.city && org.city.toLowerCase().includes(search.city.toLowerCase())
      );
    }
    
    setFilteredOrganizations(filtered);
    // Reset to first page when filters change
    setCurrentPage(1);
  }, [allOrganizations, search.name, search.city]);

  // Calculate paginated results - always show 6 initially, then 6 more each time
  const paginatedOrganizations = React.useMemo(() => {
    const totalToShow = currentPage * PAGE_SIZE;
    return filteredOrganizations.slice(0, totalToShow);
  }, [filteredOrganizations, currentPage]);

  // Determine if there are more organizations to load
  const hasMore = paginatedOrganizations.length < filteredOrganizations.length;
  
  // Pagination state tracking

  const handleLoadMore = (e) => {
    // If onLoadMoreClick is provided, call it (useful for showing signup popup)
    if (onLoadMoreClick) {
      onLoadMoreClick(e);
    } else {
      // Otherwise, just load more organizations
      setCurrentPage(prev => prev + 1);
    }
  };
  
  // Reset to first page when filtered organizations change
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredOrganizations]);

  useEffect(() => {
    fetchOrganizations();
    // eslint-disable-next-line
  }, []);

  const handleSearchChange = (e) => {
    setSearch({ ...search, [e.target.name]: e.target.value });
  };

  const handleNameSearch = (e) => {
    e.preventDefault();
  };

  const handleCardClick = (org) => {
    if (!org || !org.id) return;
    navigate(`/organization/${org.id}`);
  };

  return (
    <section className="py-10 bg-gradient-to-br from-white via-blue-50 to-blue-100 rounded-3xl shadow-xl mb-10 border border-blue-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {showHeader && (
          <div className="text-center mb-10">
            <h2 className="text-4xl font-bold bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent tracking-tight">
              {headerText}
            </h2>
            {subheaderText && (
              <p className="mt-2 text-lg text-gray-500 font-medium">{subheaderText}</p>
            )}
          </div>
        )}
        
        {showSearchControls && (
          <form onSubmit={handleNameSearch} className="z-10 bg-white/80 backdrop-blur rounded-xl shadow flex flex-wrap items-center gap-4 px-6 py-4 mb-8 border border-gray-200">
            <div className="flex-1 min-w-[200px]">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Organization Name
              </label>
              <input
                type="text"
                name="name"
                id="name"
                value={search.name}
                onChange={handleSearchChange}
                placeholder="Search by name..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                name="city"
                id="city"
                value={search.city}
                onChange={handleSearchChange}
                placeholder="City, State, or Country"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="self-end">
              <button
                type="submit"
                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium rounded-lg hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                Search
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500">{error}</p>
            <button
              onClick={fetchOrganizations}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            {filteredOrganizations.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No organizations found matching your criteria.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedOrganizations.map((org) => (
                  <div 
                    key={org.id} 
                    onClick={() => handleCardClick(org)}
                    className="cursor-pointer transform transition-all duration-200 hover:scale-[1.02]"
                  >
                    <OrganizationCard organization={org} />
                  </div>
                ))}
              </div>
            )}

            {hasMore && (
              <div className="mt-8 text-center">
                <button
                  onClick={handleLoadMore}
                  className="px-6 py-2 bg-white border-2 border-blue-500 text-blue-500 rounded-lg hover:bg-blue-50 transition-colors duration-200"
                >
                  Load More
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default FindOrganizationsTab;
